<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type EventEntry } from '../api/client'

// Bright-yellow, hard-to-miss instruction shown when an auto-SELL fell inside
// the position's T+1 settlement window and HL web dealing refused it (see
// JhDealingTickService's settlement-lock branch, 2026-08-18 requirement). The
// operator must complete the sale manually in the HL APP, which permits the
// unsettled sell that HL web blocks (channel differential, PRs #78/#79).

const events = ref<EventEntry[]>([])
let timer: ReturnType<typeof setInterval> | null = null

async function poll() {
  // Only today's instruction matters — don't pull the Event Log's 300/144h window every 10s.
  try { events.value = await api.events({ maxHours: 24 }) } catch { /* ignore transient */ }
}

onMounted(() => {
  poll()
  timer = setInterval(poll, 10000) // 10s, same cadence as the event log
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

function isToday(ts: string): boolean {
  try {
    return new Date(ts).toDateString() === new Date().toDateString()
  } catch { return false }
}

// The active instruction: the most recent (events arrive newest-first)
// 'sell_blocked_manual_action' system event dated today. The tick emits it
// exactly once per locked SELL morning; it clears naturally at the next UK
// day rollover (the position settles overnight — nothing to un-set).
const activeInstruction = computed<EventEntry | null>(() => {
  for (const ev of events.value) {
    if (ev.type === 'sell_blocked_manual_action' && isToday(ev.timestamp)) return ev
  }
  return null
})
</script>

<template>
  <div v-if="activeInstruction" class="manual-sell-banner" role="alert" data-test="manual-sell-banner">
    <span class="msb-icon">⚠️</span>
    <div class="msb-body">
      <div class="msb-title">ACTION REQUIRED — SELL MANUALLY IN THE HL APP</div>
      <div class="msb-msg">{{ activeInstruction.message }}</div>
    </div>
  </div>
</template>

<style scoped>
.manual-sell-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #ffd600;          /* bright yellow */
  color: #1a1a1a;
  border: 2px solid #c8a900;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 0.6rem;
  font-weight: 700;
  box-shadow: 0 0 0 2px rgba(255, 214, 0, 0.35);
  animation: msb-pulse 1.6s ease-in-out infinite;
}
@keyframes msb-pulse {
  0%, 100% { box-shadow: 0 0 0 2px rgba(255, 214, 0, 0.35); }
  50%      { box-shadow: 0 0 0 6px rgba(255, 214, 0, 0.55); }
}
.msb-icon { font-size: 1.5rem; flex-shrink: 0; line-height: 1; }
.msb-body { display: flex; flex-direction: column; gap: 0.15rem; }
.msb-title { font-size: 0.95rem; letter-spacing: 0.03em; }
.msb-msg { font-size: 0.85rem; font-weight: 600; }

@media (max-width: 768px) {
  .manual-sell-banner { padding: 0.6rem 0.75rem; }
  .msb-title { font-size: 0.82rem; }
  .msb-msg { font-size: 0.76rem; }
}
</style>
